import {ActionReducerMap, createFeatureSelector, createSelector} from '@ngrx/store';
import * as fromRoot from '../../reducers';
import * as fromAfbApi from '../../afb-api/reducers';
import * as fromFloorPlanEditorView from './floor-plan-editor-view.reducer';
import * as fromFloorPlanEditorDeviceMarker from './floor-plan-editor-device-marker.reducer';

import {Floor} from '../../afb-api/models/floor';
import {DeviceMarker} from '../../floor-plan-shared/models/device-marker';

export interface FloorPlanEditorState {
  floorPlanEditorView: fromFloorPlanEditorView.State;
  floorPlanEditorDeviceMarker: fromFloorPlanEditorDeviceMarker.State;
}

export interface State extends fromRoot.State {
  floorPlanEditor: FloorPlanEditorState;
}

export const reducers: ActionReducerMap<FloorPlanEditorState> = {
  floorPlanEditorView: fromFloorPlanEditorView.reducer,
  floorPlanEditorDeviceMarker: fromFloorPlanEditorDeviceMarker.reducer
};

export const floorPlanEditorState = createFeatureSelector<FloorPlanEditorState>('floor-plan-editor');

/**
 * Floor Plan Editor View State
 */
export const floorPlanEditorViewState = createSelector(
  floorPlanEditorState,
  (state) => state.floorPlanEditorView
);

export const selectedFloorId = createSelector(
  floorPlanEditorViewState,
  fromFloorPlanEditorView.selectedFloorId
);
export const uploadedImageWithSource = createSelector(
  floorPlanEditorViewState,
  fromFloorPlanEditorView.uploadedImageWithSource
);
export const didFloorImageChange = createSelector(
  floorPlanEditorViewState,
  fromFloorPlanEditorView.didFloorImageChange
);
export const saveFloorPlanLoading = createSelector(
  floorPlanEditorViewState,
  fromFloorPlanEditorView.saveFloorPlanLoading
);
export const saveFloorPlanLoaded = createSelector(
  floorPlanEditorViewState,
  fromFloorPlanEditorView.saveFloorPlanLoaded
);
export const saveFloorPlanError = createSelector(
  floorPlanEditorViewState,
  fromFloorPlanEditorView.saveFloorPlanError
);

export const selectedFloor = createSelector(
  selectedFloorId,
  fromAfbApi.floorEntities,
  (floorId: string, floorEntities: { [id: string]: Floor }) => floorId != null ? floorEntities[floorId] : null
);
export const selectedFloorDevices = createSelector(
  selectedFloor,
  (floor: Floor) => floor != null && floor.floorPlan != null && floor.floorPlan.devices != null ? floor.floorPlan.devices : []
);
export const selectedFloorPlanImageUrl = createSelector(
  selectedFloor,
  (_selectedFloor: Floor) => (_selectedFloor != null && _selectedFloor.floorPlan != null) ? _selectedFloor.floorPlan.imageUrl : null
);

/**
 * Floor Plan Editor Device Marker
 */
export const floorPlanEditorDeviceMarkerState = createSelector(
  floorPlanEditorState,
  (state) => state.floorPlanEditorDeviceMarker
);

export const deviceMarkerEntities = createSelector(
  floorPlanEditorDeviceMarkerState,
  fromFloorPlanEditorDeviceMarker.deviceMarkerEntities
);
export const initialDeviceMarkerEntities = createSelector(
  floorPlanEditorDeviceMarkerState,
  fromFloorPlanEditorDeviceMarker.initialDeviceMarkerEntities
);
export const addedDeviceMarkerUuids = createSelector(
  floorPlanEditorDeviceMarkerState,
  fromFloorPlanEditorDeviceMarker.addedDeviceMarkerUuids
);
export const updatedDeviceMarkerUuid = createSelector(
  floorPlanEditorDeviceMarkerState,
  fromFloorPlanEditorDeviceMarker.updatedDeviceMarkerUuid
);
export const removedDeviceMarkerUuids = createSelector(
  floorPlanEditorDeviceMarkerState,
  fromFloorPlanEditorDeviceMarker.removedDeviceMarkerUuids
);
export const isRemoveAllDeviceMarkerInvoked = createSelector(
  floorPlanEditorDeviceMarkerState,
  fromFloorPlanEditorDeviceMarker.isRemoveAllDeviceMarkerInvoked
);
export const isDeviceMarkerInitialized = createSelector(
  floorPlanEditorDeviceMarkerState,
  fromFloorPlanEditorDeviceMarker.isDeviceMarkerInitialized
);
export const didDeviceMarkerEntitiesChange = createSelector(
  floorPlanEditorDeviceMarkerState,
  fromFloorPlanEditorDeviceMarker.didDeviceMarkerEntitiesChange
);

export const deviceMarkerUuids = createSelector(
  deviceMarkerEntities,
  (entities: { [deviceUuid: string]: DeviceMarker }) => Object.keys(entities).map((deviceUuid: string) => deviceUuid)
);
export const initialDeviceMarkerUuids = createSelector(
  initialDeviceMarkerEntities,
  (entities: { [deviceUuid: string]: DeviceMarker }) => Object.keys(entities).map((deviceUuid: string) => deviceUuid)
);
export const hasDeviceMarkers = createSelector(
  deviceMarkerUuids,
  initialDeviceMarkerUuids,
  (_deviceMarkerUuids: string[], _initialDeviceMarkerUuids: string[]) =>
    (_deviceMarkerUuids.length > 0 || _initialDeviceMarkerUuids.length > 0)
);
export const addedDeviceMarkers = createSelector(
  deviceMarkerEntities,
  addedDeviceMarkerUuids,
  (entities: { [deviceUuid: string]: DeviceMarker }, deviceUuids: string[]) =>
    deviceUuids != null ? deviceUuids.map((deviceUuid: string) => entities[deviceUuid]) : null
);
export const updatedDeviceMarker = createSelector(
  deviceMarkerEntities,
  updatedDeviceMarkerUuid,
  (entities: { [deviceUuid: string]: DeviceMarker }, deviceUuid: string) => entities[deviceUuid]
);
export const deviceMarkersAsFloorPlanDevices = createSelector(
  deviceMarkerEntities,
  (entities: { [deviceUuid: string]: DeviceMarker }) => {
    const floorPlanDevices = Object.keys(entities)
      .map((deviceUuid: string) => entities[deviceUuid])
      .map((deviceMarker: DeviceMarker) => {
        const {name: removedProperty, ...floorPlanDevice} = deviceMarker;
        return floorPlanDevice;
      });
    return floorPlanDevices;
  }
);

/**
 * Hybrid
 */
export const isFloorPlanEdited = createSelector(
  didFloorImageChange,
  didDeviceMarkerEntitiesChange,
  (_didFloorImageChange: boolean, _didDeviceMarkerEntitiesChange: boolean) => {
    return (_didFloorImageChange || _didDeviceMarkerEntitiesChange);
  }
);
